/**
 * Upscale Service
 *
 * Calls the upscale-submit and upscale-status Edge Functions to upscale
 * staged/empty images using the Freepik Magnific Precision v2 API.
 *
 * The upscaling process is async:
 * 1. Submit job → get taskId
 * 2. Poll for status until COMPLETED or FAILED
 */

import { supabase } from '@/lib/supabase';

// ============================================================================
// Types
// ============================================================================

interface UpscaleSubmitResponse {
  success: boolean;
  taskId?: string;
  error?: string;
  code?: string;
}

interface UpscaleStatusResponse {
  success: boolean;
  status: 'pending' | 'completed' | 'failed';
  imageUrl?: string;
  storagePath?: string;
  error?: string;
}

// ============================================================================
// Constants
// ============================================================================

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 60; // 5 min max

// ============================================================================
// Submit Upscale Job
// ============================================================================

/**
 * Submit an upscaling job to the Edge Function.
 * Returns a taskId for polling.
 */
export const submitUpscale = async (
  imageStoragePath: string,
  projectId: string,
  imageId: string,
  variant: 'staged' | 'empty',
  userId?: string
): Promise<UpscaleSubmitResponse> => {
  console.log(`[Upscale] Submitting ${variant} image for upscaling...`);
  console.log(`[Upscale] Storage path: ${imageStoragePath}`);
  console.log(`[Upscale] Image DB ID: ${imageId}`);

  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke<UpscaleSubmitResponse>(
    'upscale-submit',
    {
      body: { imageStoragePath, projectId, imageId, variant, userId },
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    }
  );

  if (error) {
    console.error('[Upscale] Submit error:', error);
    throw new Error(`Upscaling fehlgeschlagen: ${error.message}`);
  }

  if (!data || !data.success) {
    const errorMsg = data?.error || 'Unbekannter Fehler';
    console.error('[Upscale] Submit failed:', errorMsg);
    throw new Error(errorMsg);
  }

  console.log(`[Upscale] Job submitted. Task ID: ${data.taskId}`);
  return data;
};

// ============================================================================
// Check Upscale Status
// ============================================================================

/**
 * Check the status of an upscaling job.
 * Returns 'pending', 'completed', or 'failed'.
 */
export const checkUpscaleStatus = async (
  taskId: string,
  projectId: string,
  imageId: string,
  variant: 'staged' | 'empty',
  userId?: string
): Promise<UpscaleStatusResponse> => {
  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke<UpscaleStatusResponse>(
    'upscale-status',
    {
      body: { taskId, projectId, imageId, variant, userId },
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    }
  );

  if (error) {
    console.error('[Upscale] Status check error:', error);
    throw new Error(`Status-Abfrage fehlgeschlagen: ${error.message}`);
  }

  if (!data) {
    throw new Error('Keine Antwort vom Server');
  }

  return data;
};

// ============================================================================
// Upscale with Polling (High-Level Orchestrator)
// ============================================================================

/**
 * Submit an upscaling job and poll until completion.
 * This is the main function called from App.tsx.
 *
 * @param imageStoragePath - Path to the staged/empty image in Supabase Storage
 * @param projectId - The project ID
 * @param imageId - The database ID of the image record
 * @param variant - 'staged' or 'empty'
 * @param userId - User ID for credit deduction
 * @param onProgress - Optional callback for progress updates
 * @returns Object with imageUrl on success
 */
export const upscaleImageWithPolling = async (
  imageStoragePath: string,
  projectId: string,
  imageId: string,
  variant: 'staged' | 'empty',
  userId?: string,
  onProgress?: (status: string) => void
): Promise<{ success: boolean; imageUrl?: string; storagePath?: string; error?: string }> => {
  console.log(`[Upscale] Starting upscale with polling for ${variant} image...`);

  // Step 1: Submit the job
  onProgress?.('Wird hochgeladen...');
  const submitResult = await submitUpscale(imageStoragePath, projectId, imageId, variant, userId);

  if (!submitResult.taskId) {
    throw new Error('Kein Task-ID erhalten');
  }

  const taskId = submitResult.taskId;
  console.log(`[Upscale] Polling for task ${taskId}...`);

  // Step 2: Poll for completion
  let attempts = 0;

  while (attempts < MAX_POLL_ATTEMPTS) {
    onProgress?.('Wird hochskaliert...');

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    attempts++;

    console.log(`[Upscale] Poll attempt ${attempts}/${MAX_POLL_ATTEMPTS}...`);

    const statusResult = await checkUpscaleStatus(taskId, projectId, imageId, variant, userId);

    if (statusResult.status === 'completed') {
      console.log(`[Upscale] Upscaling complete! URL: ${statusResult.imageUrl}`);
      onProgress?.('Fertig!');
      return {
        success: true,
        imageUrl: statusResult.imageUrl,
        storagePath: statusResult.storagePath,
      };
    }

    if (statusResult.status === 'failed') {
      const errorMsg = statusResult.error || 'Upscaling fehlgeschlagen';
      console.error(`[Upscale] Upscaling failed: ${errorMsg}`);
      throw new Error(errorMsg);
    }

    // Still pending, continue polling
  }

  // Timeout
  throw new Error('Upscaling-Zeitlimit uberschritten. Bitte versuchen Sie es erneut.');
};
